import { Badge, MantineColor, Tooltip } from '@mantine/core';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { OrderDetailsResponse, OrderStatus } from '../data-access/types/orders';

interface OrderStatusProps {
  status: OrderStatus;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'filled' | 'light' | 'outline' | 'dot';
}

const getOrderStatusColor = (status: OrderStatus): MantineColor => {
  switch (status) {
    case OrderStatus.Created:
      return 'blue';
    case OrderStatus.Received:
      return 'cyan';
    case OrderStatus.InProgress:
      return 'orange';
    case OrderStatus.PartiallyCompleted:
      return 'yellow';
    case OrderStatus.Completed:
      return 'green';
    case OrderStatus.Canceled:
      return 'red';
    default:
      return 'gray';
  }
};

const getOrderStatusLabel = (status: OrderStatus): string => {
  switch (status) {
    case OrderStatus.Created:
      return 'sampling.order-status.created';
    case OrderStatus.Received:
      return 'sampling.order-status.received';
    case OrderStatus.InProgress:
      return 'sampling.order-status.in-progress';
    case OrderStatus.PartiallyCompleted:
      return 'sampling.order-status.partially-completed';
    case OrderStatus.Completed:
      return 'sampling.order-status.completed';
    case OrderStatus.Canceled:
      return 'sampling.order-status.canceled';
    default:
      return 'common:unknown';
  }
};

/**
 * Derives an overall order status from the order's progress flags
 */
const deriveOrderStatus = (order: OrderDetailsResponse): OrderStatus => {
  // If results are received, order is completed
  if (order.received_results) {
    return OrderStatus.Completed;
  }

  // If samples are received at lab, order is in progress
  if (order.received_laboratory) {
    return OrderStatus.InProgress;
  }

  // If sampling is done, order is partially completed
  if (order.sampled) {
    return OrderStatus.PartiallyCompleted;
  }

  // If sampler is found, order is in progress
  if (order.sampler_found) {
    return OrderStatus.InProgress;
  }

  // If plan is created, order is received/accepted
  if (order.plan_created) {
    return OrderStatus.Received;
  }

  // Default to created
  return OrderStatus.Created;
};

export const OrderStatusPill: FC<OrderStatusProps> = ({
  status,
  size = 'md',
  variant = 'light',
}) => {
  const { t } = useTranslation();

  const label = t(getOrderStatusLabel(status));

  return (
    <Tooltip label={label} withArrow>
      <Badge
        color={getOrderStatusColor(status)}
        size={size}
        title={label}
        variant={variant}
      >
        {label}
      </Badge>
    </Tooltip>
  );
};

export { getOrderStatusColor, getOrderStatusLabel, deriveOrderStatus };
export default OrderStatusPill;
